<template>
  <div class="report-table">
    <div cols="4" class="report-table__left-column">
      <b-row class="report-table__left-header">
        <b-col
          class="report-table__col report-table__gray report-table__title"
          cols="12"
        >
          <div class="w-75 text-center pr-2">
            {{ $t('engineering.norm') }}
          </div>
        </b-col>
        <b-col
          v-if="isParameters"
          cols="12"
          class="report-table__col report-table__title report-table__equipment-header"
        >
          <div class="w-75 text-center pr-2">
            {{ $t('engineering.reports.equipment-category') }}
          </div>
        </b-col>
        <b-col class="report-table__col report-table__title justify-content-center">
          {{ $t('engineering.reports.test') }}
        </b-col>
        <b-col
          v-if="showValues"
          class="report-table__value report-table__gray report-table__title"
          cols="3"
        >
          {{ $t('general.value') }}
        </b-col>
      </b-row>
      <b-row
        v-for="field in testsFields"
        class="report-table__col"
        :key="field.key"
      >
        <b-col
          align-v="center"
        >
          <b-row>
            <b-col class="report-table__text" cols="auto">
              {{ $t(field.text, field.textParams) }}
              <info-badge
                v-if="field.tooltip"
                :translation-key="field.tooltip"
              />
            </b-col>
          </b-row>
        </b-col>
        <b-col v-if="showValues" class="report-table__value p-0 report-table__gray" cols="3">
          <span
            class="report-table__text text-truncate"
            :title="tableValueProxy(table.tests, field.key, true)">
            {{ tableValueProxy(table.tests, field.key, true) }}
            <info-badge
              v-if="isDbpcInhibitorContentOriginalPercentage(field.key) && !!table.tests[field.key]"
              tooltip-custom-class="report-table__value-dbpc-inhibitor-tooltip"
              :string-key="getDbpcInhibitorTooltip(table.tests, field.key)"
            />
          </span>
        </b-col>
      </b-row>
    </div>
    <b-row class="report-table__right-column-wrapper">
      <b-col
        v-for="(norm, ruleIndex) in table.byRules"
        class="report-table__right-col"
        :key="ruleIndex"
        :class="{ 'report-table__parameter-column': isParameters }"
        cols="auto"
      >
        <b-row class="report-table__title report-table__gray" align-h="center" align-v="center">
          <b-col class="report-table__align-center" cols="12">
            <physical-chemical-flex-table-title
              :norm="norm"
              :show-custom-name-below="norm.custom"
            />
          </b-col>
        </b-row>
        <b-row class="report-table__right-subcolumn">
          <b-col
            v-if="isParameters"
            cols="12"
            class="report-table__right-tension"
          >
            <b-row
              class="report-table__row"
              align-h="center"
              align-v="center"
            >
              <span>{{ norm.tensionClass }}</span>
            </b-row>
          </b-col>
          <b-col
            v-for="(values, normIndex) in norm.rules"
            :key="normIndex"
            :cols="ruleColsHandler(norm.rules.length)"
          >
            <b-row
              class="report-table__row report-table__gray-ligthen report-table__diagnostic"
              :class="{ 'report-table__diagnostic_parameters': isParameters }"
              align-h="center"
              align-v="center"
            >
              <span class="report-table__text report-table__diagnostic">
                {{ diagnosticRuleLabel(values.diagnosticRule) }}
              </span>
            </b-row>
            <b-row
              v-for="item in testsFields"
              class="report-table__row"
              align-h="center"
              align-v="center"
              :key="item.description"
            >
              <b-col
                class="report-table__align-center"
                align-h="center"
                cols="12"
                :class="{ 'px-1': isAlarmedValue(values, item.key)}"
              >
                <classification
                  v-if="showClassification(values[item.key])"
                  :classification="values[item.key].classification"
                  show-label
                  show-tooltip
                />
                <span
                  class="text-truncate"
                  :title="tableValueProxy(values, item.key)"
                >
                  {{ tableValueProxy(values, item.key) }}
                </span>
                <info-badge
                  class="report-table__info-badge"
                  v-if="!isParameters && hasTooltip(values[item.key])"
                  :string-key="tableValueProxy(values, item.key)"
                />
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { diagnosticRule } from 'sigma-mappings';
import InfoBadge from '@/components/icon/InfoBadge';
import GeneralService from '@/domain/services/installation/engineering/physicalChemical/generalTab';
import { getUniqueId } from '@/services/createId';
import Classification from '@/components/Classification';
import PhysicalChemicalFlexTableTitle from './PhysicalChemicalFlexTableTitle';

const rulesLabels = {
  [diagnosticRule.UNDEFINED]: 'enums.engineering.diagnostic-rule.undefined',
  [diagnosticRule.STANDARD]: 'enums.engineering.diagnostic-rule.standard',
  [diagnosticRule.UNSATISFACTORY]: 'enums.engineering.diagnostic-rule.unsatisfactory',
  [diagnosticRule.HARMFUL]: 'enums.engineering.diagnostic-rule.harmful',
};

const EMPTY_VALUE = '--';
const OK_VALUE = 'OK';
const ORIGINAL_CONTENT_PARAMETERIZED = 0.3;

export default {
  name: 'FlexTable',
  components: {
    InfoBadge,
    PhysicalChemicalFlexTableTitle,
    Classification,
  },
  props: {
    testsFields: {
      type: Array,
      default: () => [],
    },
    table: {
      type: Object,
      default: () => ({}),
    },
    isParameters: {
      type: Boolean,
      default: false,
    },
    showValues: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    columnsTableVertical() {
      return this.table.tableVertical.map(({ text }) => text);
    },
  },
  methods: {
    showClassification(item = {}) {
      return !this.isParameters && item.classification;
    },
    getUniqueId,
    validateIfObjKeyHasValue(testObject, field, value) {
      const keyValue = testObject[field];
      return keyValue !== undefined && (value === undefined || value == null);
    },
    isAlarmedValue(values, key) {
      const value = values[key];
      const { isAlarmed, fieldsWithDiagnosis } = values;
      return !this.isParameters
        ? isAlarmed
        && value
        && value.description !== OK_VALUE
        : fieldsWithDiagnosis.includes(key);
    },
    tableValueClass(values, key) {
      const showAlarmed = this.isAlarmedValue(values, key);
      return {
        'text-red': showAlarmed,
        'text-green': !showAlarmed,
      };
    },
    hasClassification({ classification }) {
      if (!classification) {
        return false;
      }
      return classification;
    },
    hasTooltip(text) {
      return !!text && text.description !== OK_VALUE;
    },
    ruleColsHandler(value) {
      return 12 / value;
    },
    diagnosticRuleLabel(value) {
      if (!this.isParameters) {
        return this.$t('engineering.report');
      }
      return this.$t(rulesLabels[value]);
    },
    getBooleanFieldsLabel(value, field) {
      const translationResolver = GeneralService.sampleListRenderingHelpers
        .valueTranslationPathResolvers[field];
      if (translationResolver) {
        return this.$t(translationResolver(value));
      }
      return value;
    },
    calcDbpcInhibitorContentOriginalPercentage(value) {
      return (value / ORIGINAL_CONTENT_PARAMETERIZED) * 100;
    },
    isDbpcInhibitorContentOriginalPercentage(field) {
      return field === 'dbpcInhibitorContentOriginalPercentage';
    },
    getFieldValue(value, field, isValueCollumn) {
      const valueType = typeof value;
      let returnValue = value;

      if (isValueCollumn
        && typeof value === 'number'
        && this.isDbpcInhibitorContentOriginalPercentage(field)) {
        returnValue = this.calcDbpcInhibitorContentOriginalPercentage(returnValue);
      }

      const processedValue = {
        number: this.$n(returnValue),
        boolean: this.getBooleanFieldsLabel(returnValue, field),
      };

      return processedValue[valueType] || returnValue.description;
    },
    tableValueProxy(values, field, isValueCollumn) {
      const value = values[field];
      if (value != null
        && this.getFieldValue(value, field) === OK_VALUE) {
        return this.$t('engineering.normal');
      }
      if (value != null) {
        return this.getFieldValue(value, field, isValueCollumn);
      }
      return EMPTY_VALUE;
    },
    getDbpcInhibitorTooltip(values, field) {
      const sample = values[field];
      const calculatedValue = this.calcDbpcInhibitorContentOriginalPercentage(sample);
      return this.$t(
        'fields.physical-chemical-analysis.percentage-original-dbpc-inhibitor-tooltip',
        {
          sample: this.$n(sample),
          originalContentParameterized: this.$n(ORIGINAL_CONTENT_PARAMETERIZED),
          calculatedValue: this.$n(calculatedValue),
        },
      );
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/variables"
@import "src/styles/defaults/mobile-first"

$cell-height: 60px

.report-table
  overflow: auto
  height: 600px
  position: relative

  &__left-header
    position: sticky
    left: 0
    top: 0
    z-index: 10
    border-bottom: 1px solid $color-border-gray

    .report-table
      &__title:first-child
        border-right-width: 1px

      &__title:not(:first-child)
        border-left: 1px solid white
        border-top: 0
        border-right: 0
        border-bottom: 0

        &.report-table__equipment-header
          border-right-width: 1px

      &__value
        border-left-width: 1px !important
        border-right-width: 1px !important

  &__left-column
    left: 0
    top: 0
    z-index: 10
    flex-shrink: 0
    display: inline-flex
    width: 400px
    flex-direction: column
    padding-left: 15px
    border-bottom: 1px solid $color-border-gray

    .report-table__value
      margin-top: -2px
      border-left-width: 1px
      max-width: 100px

      &.report-table__title
        margin-top: 0
        border-left: 1px solid white
        border-right: 1px solid white

    +media-min-md
      position: sticky

  &__right-column-wrapper
    display: inline-flex
    flex-wrap: nowrap
    position: absolute
    top: 0
    left: 428px

  &__right-tension
    position: sticky
    top: $cell-height
    z-index: 5

    .report-table__row
      border-right: 1px solid white !important
      border-bottom: 1px solid white !important

  &__right-subcolumn
    display: flex

  &__right-col
    width: 310px
    border-bottom: 1px solid $color-border-gray

    &:last-child
      .report-table__row:not(.report-table__diagnostic)
        border-right: 1px solid $color-border-gray

  &__parameter-column:last-child
    border-right: 1px solid white

  &__parameter-column
    width: 310px

  &__align-center
    display: flex
    align-items: center
    justify-content: center
    width: 100%

  &__row
    padding: 6px
    border: 1px solid white
    background-color: var(--color-table-body-accent-bg)

    &:nth-child(2n)
      background-color: var(--color-background-light)

    &:not(.report-table__diagnostic)
      border-bottom: 1px solid white
      border-top: 0

  &__col
    display: flex
    align-items: center
    background-color: var(--color-table-body-accent-bg)

    &:nth-child(2n)
      background-color: var(--color-background-light)

    &:not(.report-table__title)
      border-left: 1px solid $color-border-gray
      border-bottom: 1px solid white

  &__value
    border: 1px solid #FFF
    display: flex
    align-items: center
    justify-content: center
    border-bottom: 0

  &__gray
    background: var(--color-table-header-bg)

  &__title
    border: 1px solid #FFF
    font-size: 1.14rem
    font-weight: bold
    color: var(--font-color-primary)
    position: sticky
    top: 0
    z-index: 5

  &__sub-title
    font-size: 1.14rem
    font-weight: normal
    font-style: italic
    color: var(--font-color-primary)

  &__diagnostic
    position: sticky
    top: $cell-height
    z-index: 5

    &_parameters
      top: 2 * $cell-height
      border-bottom: 1px solid $color-border-gray !important

  &__diagnostic.report-table__row, &__diagnostic_parameters.report-table__row
    border-bottom: 1px solid $color-border-gray
    min-height: $cell-height + 1
    border-top: 0

  &__text
    font-size: 1rem
    color: var(--font-color-primary)
    font-weight: 600
    max-height: 60px
    overflow-y: auto

  &__text.text-truncate
    padding: 0 5px

  &__info-badge
    padding: 0 5px

  &__col,
  &__value,
  &__row,
  &__title
    min-height: $cell-height
    max-height: $cell-height
    height: $cell-height

</style>
<style lang="sass">
.report-table__value-dbpc-inhibitor-tooltip
  & .tooltip-inner
    text-align: left
</style>
